import type { EmailQueueItem } from '@/pages/admin/email-queue/page';

interface Props {
  email: EmailQueueItem;
  onClose: () => void;
}

export default function DetailModal({ email, onClose }: Props) {
  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  function statusBadge(status: string) {
    const map: Record<string, string> = {
      pending:    'bg-amber-100 text-amber-700 border-amber-200',
      processing: 'bg-blue-100 text-blue-700 border-blue-200',
      sent:       'bg-emerald-100 text-emerald-700 border-emerald-200',
      failed:     'bg-rose-100 text-rose-700 border-rose-200',
    };
    return map[status] ?? map.pending;
  }

  function renderMetadata(value: unknown): string {
    if (value === null || value === undefined) return '\u2014';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      {/* Card */}
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100">
              <i className="ri-mail-settings-line text-slate-500 text-lg"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Email Details</h3>
              <p className="text-[10px] text-slate-400 font-mono">{email.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* ID */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">ID</p>
            <p className="text-xs font-mono text-slate-600 break-all">{email.id}</p>
          </div>

          {/* Recipient */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Recipient Email</p>
            <p className="text-sm text-slate-800 font-medium">{email.to_email}</p>
          </div>

          {/* Subject */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Subject</p>
            <p className="text-sm text-slate-800">{email.subject || '\u2014'}</p>
          </div>

          {/* Status + Sent At row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Status</p>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold border ${statusBadge(email.status)}`}>
                {email.status}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Sent At</p>
              <p className="text-xs text-slate-600">{formatDate(email.sent_at)}</p>
            </div>
          </div>

          {/* Created At */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Created At</p>
            <p className="text-xs text-slate-600">{formatDate(email.created_at)}</p>
          </div>

          {/* Error Message */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Error Message</p>
            {email.last_error ? (
              <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                <p className="text-xs text-rose-700 font-mono leading-relaxed whitespace-pre-wrap break-all">{email.last_error}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-300">\u2014</p>
            )}
          </div>

          {/* HTML Content Preview */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">HTML Content</p>
            {email.body ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <p className="text-[11px] text-slate-600 font-mono leading-relaxed whitespace-pre-wrap break-all">
                  {email.body.length > 1200 ? email.body.slice(0, 1200) + '\n\n... (truncated)' : email.body}
                </p>
              </div>
            ) : (
              <p className="text-xs text-slate-300">\u2014</p>
            )}
          </div>

          {/* Metadata */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Metadata</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
              <pre className="text-[11px] text-slate-600 font-mono leading-relaxed whitespace-pre-wrap break-all">
                {renderMetadata(email.metadata)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}